import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { Loader2, AlertCircle, Sparkles, TrendingUp, ArrowLeft } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import type { AnalysisResult } from '../types/acquisitionAnalysis';

// Import the same result view components used in the main page
import { ExecutiveSummary } from '../components/acquisitionAnalysis/sections/ExecutiveSummary';
import { TrailIncomeYoY } from '../components/acquisitionAnalysis/sections/TrailIncomeYoY';
import { FYForecast } from '../components/acquisitionAnalysis/sections/FYForecast';
import { SeasonalityComparison } from '../components/acquisitionAnalysis/sections/SeasonalityComparison';
import { ValuationSummary } from '../components/acquisitionAnalysis/sections/ValuationSummary';
import { BrokerPerformance } from '../components/acquisitionAnalysis/sections/BrokerPerformance';
import { BrokerGrowthTrajectory } from '../components/acquisitionAnalysis/sections/BrokerGrowthTrajectory';
import { RiskAssessment } from '../components/acquisitionAnalysis/sections/RiskAssessment';
import { SummaryAssessmentSection } from '../components/acquisitionAnalysis/sections/SummaryAssessmentSection';

type TabId =
  | 'executive'
  | 'trail-yoy'
  | 'forecast'
  | 'seasonality'
  | 'valuation'
  | 'brokers'
  | 'growth'
  | 'risk'
  | 'summary';

const TABS = [
  { id: 'executive' as TabId, label: 'Executive Summary' },
  { id: 'trail-yoy' as TabId, label: 'Trail Income YoY' },
  { id: 'forecast' as TabId, label: 'FY Forecast' },
  { id: 'seasonality' as TabId, label: 'Seasonality' },
  { id: 'valuation' as TabId, label: 'Valuation' },
  { id: 'brokers' as TabId, label: 'Broker Performance' },
  { id: 'growth' as TabId, label: 'Growth Trajectory' },
  { id: 'risk' as TabId, label: 'Risk Assessment' },
  { id: 'summary' as TabId, label: 'Summary' },
];

const SharedAnalysisPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('executive');

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No share token provided');
        setIsLoading(false);
        return;
      }

      try {
        const validateFunc = httpsCallable<
          { token: string },
          any
        >(functions, 'validateShareToken');

        const response = await validateFunc({ token });
        const data = response.data;

        // The backend returns the full AnalysisDocument which contains a 'result' field
        if (data.result) {
          setResult(data.result);
        } else if (data.status === 'completed') {
          // Fallback: if result is at top level (shouldn't happen but handle it)
          setResult(data as AnalysisResult);
        } else {
          setError('Analysis data not available');
        }
      } catch (err: any) {
        console.error('Failed to validate share token:', err);

        // Extract error message
        if (err.code === 'functions/permission-denied') {
          setError('This share link is invalid or has expired');
        } else if (err.code === 'functions/not-found') {
          setError('Analysis not found');
        } else if (err.code === 'functions/failed-precondition') {
          setError(err.message || 'Analysis is not ready');
        } else {
          setError(err.message || 'Failed to load shared analysis');
        }
      } finally {
        setIsLoading(false);
      }
    };

    validateToken();
  }, [token]);

  const renderTabContent = () => {
    if (!result) return null;

    switch (activeTab) {
      case 'executive':
        return result.executiveSummary ? <ExecutiveSummary data={result.executiveSummary} /> : <p className="text-slate-400">Executive summary not available</p>;
      case 'trail-yoy':
        return result.trailIncomeYoY ? <TrailIncomeYoY data={result.trailIncomeYoY} /> : <p className="text-slate-400">Trail income data not available</p>;
      case 'forecast':
        return result.fyForecast ? <FYForecast data={result.fyForecast} /> : <p className="text-slate-400">Forecast data not available</p>;
      case 'seasonality':
        return result.seasonality ? <SeasonalityComparison data={result.seasonality} /> : <p className="text-slate-400">Seasonality data not available</p>;
      case 'valuation':
        return result.valuationSummary ? <ValuationSummary data={result.valuationSummary} /> : <p className="text-slate-400">Valuation data not available</p>;
      case 'brokers':
        return result.brokerPerformance ? <BrokerPerformance data={result.brokerPerformance} /> : <p className="text-slate-400">Broker performance data not available</p>;
      case 'growth':
        return result.brokerPerformance?.growthTrajectory ? <BrokerGrowthTrajectory data={result.brokerPerformance.growthTrajectory} /> : <p className="text-slate-400">Growth trajectory data not available</p>;
      case 'risk':
        return result.riskAssessment ? <RiskAssessment data={result.riskAssessment} /> : <p className="text-slate-400">Risk assessment data not available</p>;
      case 'summary':
        return result.summaryAssessment ? <SummaryAssessmentSection data={result.summaryAssessment} /> : <p className="text-slate-400">Summary assessment not available</p>;
      default:
        return null;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-300 text-lg">Loading shared analysis...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-slate-800/50 backdrop-blur-sm rounded-2xl p-8 border border-red-500/30 text-center">
          <div className="p-4 bg-red-500/20 rounded-full w-fit mx-auto mb-4">
            <AlertCircle className="h-10 w-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Unable to Load Analysis</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-semibold transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  // Success state - show analysis
  return (
    <div className="min-h-screen bg-slate-900">
      <div>
        <PageHeader
          icon={TrendingUp}
          title="Trail Book Acquisition Analysis"
          subtitle="Shared Report - Read Only"
        />

        <div className="px-8 pb-8">
        {/* Analysis Content */}
        {result && (
          <div className="space-y-6">
            {/* Header Card */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">{result.targetName}</h2>
                  <p className="text-sm text-slate-400">
                    Analysis completed on {new Date(result.analysisDate).toLocaleDateString()}
                  </p>
                  <p className="text-xs text-slate-500">Data period: {result.dataPeriod}</p>
                </div>
                <div className={`px-4 py-2 rounded-lg font-semibold ${
                  result.verdict === 'STRONG_BUY' ? 'bg-green-500/20 text-green-400' :
                  result.verdict === 'BUY' ? 'bg-emerald-500/20 text-emerald-400' :
                  result.verdict === 'CAUTION' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {result.verdict}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
              <div className="border-b border-slate-700/50">
                <div className="flex flex-wrap">
                  {TABS.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                        activeTab === tab.id
                          ? 'border-indigo-500 text-indigo-400 bg-indigo-500/10'
                          : 'border-transparent text-slate-400 hover:text-slate-300 hover:bg-slate-700/30'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">{renderTabContent()}</div>
            </div>

            {/* Footer */}
            <div className="text-center py-6 text-slate-500 text-sm">
              <p>This is a shared, read-only view of the analysis.</p>
              <p className="mt-1">
                <a href="https://valutten.com" target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300">
                  Learn more about VALUTTEN
                </a>
              </p>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default SharedAnalysisPage;
